import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Copy, Filter, Share2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import RecipeModal from "@/components/RecipeModal";
import MenuHealthDashboard from "@/components/MenuHealthDashboard";
import ShareMenuPanel from "@/components/ShareMenuPanel";
import { HelpTip } from "@/components/ui/help-tip";
import { formatCurrency } from "@/lib/currencyUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useTranslation } from "react-i18next";
import usePageTitle from "@/hooks/usePageTitle";

const DEPARTMENTS = [
  { value: "All", label: "All Departments", emoji: "🏢" },
  { value: "Kitchen", label: "Kitchen", emoji: "👨‍🍳" },
  { value: "Bar", label: "Bar", emoji: "🍸" },
  { value: "Lab", label: "Lab (Brewery/Fermentation)", emoji: "🧪" },
  { value: "Bakery", label: "Bakery", emoji: "🥖" },
  { value: "Pastry", label: "Pastry/Desserts", emoji: "🍰" }
];

const RecipeBank = ({ settings }) => {
  usePageTitle("pageTitles.recipes");
  const { t } = useTranslation();
  const [recipes, setRecipes] = useState([]);
  const [menuHealth, setMenuHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    fetchRecipes();
    fetchMenuHealth();
  }, [selectedDepartment]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      const params = selectedDepartment !== "All" ? `?department=${selectedDepartment}` : "";
      const response = await axios.get(`${API}/recipes${params}`);
      setRecipes(response.data);
    } catch (error) {
      console.error("Error fetching recipes:", error);
      toast.error(t("toasts.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuHealth = async () => {
    try {
      const response = await axios.get(`${API}/recipes/menu-health/dashboard`);
      setMenuHealth(response.data);
    } catch (error) {
      console.error("Error fetching menu health:", error);
    }
  };

  const handleAddRecipe = () => {
    setEditingRecipe(null);
    setIsModalOpen(true);
  };

  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (recipe) => {
    setRecipeToDelete(recipe);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recipeToDelete) return;

    try {
      await axios.delete(`${API}/recipes/${recipeToDelete.id}`);
      toast.success(t("toasts.recipeDeleted"));
      fetchRecipes();
      fetchMenuHealth();
    } catch (error) {
      console.error("Error deleting recipe:", error);
      toast.error(t("toasts.recipeFailed"));
    } finally {
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
    }
  };

  const handleToggleActive = async (recipeId, currentStatus) => {
    try {
      await axios.patch(`${API}/recipes/${recipeId}/toggle-active`);
      toast.success(currentStatus ? t("toasts.recipeDeactivated") : t("toasts.recipeActivated"));
      fetchRecipes();
      fetchMenuHealth();
    } catch (error) {
      console.error("Error toggling active status:", error);
      toast.error(t("toasts.recipeToggleFailed"));
    }
  };

  const handleDuplicate = async (recipeId) => {
    setDuplicating(recipeId);
    try {
      await axios.post(`${API}/recipes/${recipeId}/duplicate`);
      toast.success(t("recipeBank.duplicateSuccess"));
      fetchRecipes();
      fetchMenuHealth();
    } catch (error) {
      console.error("Error duplicating recipe:", error);
      toast.error(t("recipeBank.duplicateError"));
    } finally {
      setDuplicating(null);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    setIsModalOpen(false);
    setEditingRecipe(null);
    if (shouldRefresh) {
      fetchRecipes();
      fetchMenuHealth();
    }
  };

  const getProfitColor = (profitPercent, targetMargin) => {
    const profit = parseFloat(profitPercent);
    if (profit >= targetMargin) return "text-green-500";
    if (profit >= targetMargin * 0.85) return "text-amber-500";
    return "text-red-500";
  };

  const getProfitIcon = (profitPercent, targetMargin) => {
    const profit = parseFloat(profitPercent);
    if (profit >= targetMargin) return <TrendingUp className="h-4 w-4 text-green-500" />;
    return <TrendingDown className="h-4 w-4 text-amber-500" />;
  };

  const currency = settings?.currency || "USD";
  const targetMargin = settings?.target_margin || 70;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="recipe-lab">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-amber-500 font-manrope">
                {t("recipeBank.title")}
              </h1>
              <p className="text-slate-400 mt-1">{t("recipeBank.subtitle")}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right mr-4">
                <div className="text-sm text-slate-400 flex items-center justify-end">
                  {t("common.targetMargin")}
                  <HelpTip
                    title="Target Margin"
                    side="bottom"
                    align="end"
                    testId="help-target-margin"
                  >
                    The minimum profit % you want each active recipe to clear.
                    Recipes above this line are <span className="text-emerald-400">Engines</span>;
                    those below are <span className="text-amber-400">Artistic</span>. Adjust this
                    in <strong>Settings → Target Margin</strong>.
                  </HelpTip>
                </div>
                <div className="text-lg font-semibold text-amber-500">{targetMargin}%</div>
              </div>
              <Button
                type="button"
                onClick={() => setShareOpen(true)}
                className="bg-slate-800 hover:bg-slate-700 text-amber-400 border border-amber-500/40 hover:border-amber-400 font-semibold flex items-center gap-2"
                data-testid="open-share-menu-button"
              >
                <Share2 className="h-4 w-4" />
                Share Menu
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Menu Health Dashboard */}
        {menuHealth && menuHealth.total_active_recipes > 0 && (
          <MenuHealthDashboard 
            menuHealth={menuHealth} 
            currency={currency}
            className="mb-8"
          />
        )}

        {/* Action Bar with Department Filter */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">{t("recipeBank.yourRecipes")}</h2>
            <p className="text-slate-400 mt-1">
              {t("recipeBank.recipesCreated", { count: recipes.length })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Department Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-amber-500" />
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger 
                  className="bg-slate-800 border-slate-700 text-white w-[240px]"
                  data-testid="department-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept.value} value={dept.value} className="text-white">
                      {dept.emoji} {dept.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleAddRecipe}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              data-testid="add-recipe-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t("recipeBank.createRecipe")}
            </Button>
          </div>
        </div>

        {/* Recipes Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-slate-400 text-lg">{t("common.loading")}</div>
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-12 bg-slate-900/50 rounded-lg border border-slate-800">
            <div className="text-slate-400 text-lg mb-4">{t("recipeBank.noRecipes")}</div>
            <p className="text-slate-500 mb-6">
              {t("recipeBank.noRecipesDesc")}
            </p>
            <Button
              onClick={handleAddRecipe}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t("recipeBank.createFirstRecipe")}
            </Button>
          </div>
        ) : (
          <div className="bg-slate-900/50 rounded-lg border border-slate-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="recipes-table">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-900">
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("recipeBank.active")}
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("recipeBank.recipe")}
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.category")}
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("recipeBank.cost")}
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("recipeBank.menuPrice")}
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("recipeBank.plateProfitPercent")}
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-slate-300">
                      {t("pantry.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recipes.map((recipe) => (
                    <tr
                      key={recipe.id}
                      className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors"
                      data-testid={`recipe-row-${recipe.id}`}
                    >
                      <td className="py-4 px-6">
                        <Switch
                          checked={recipe.is_active}
                          onCheckedChange={() => handleToggleActive(recipe.id, recipe.is_active)}
                          className="data-[state=checked]:bg-green-500"
                          data-testid={`active-toggle-${recipe.id}`}
                        />
                      </td>
                      <td className="py-4 px-6">
                        <div className="font-medium text-white flex items-center gap-2">
                          {recipe.name}
                          {recipe.is_base_ingredient && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
                              Base Ingredient
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-400 mt-1">
                          {recipe.ingredients.length} ingredient{recipe.ingredients.length !== 1 ? "s" : ""}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">
                          {recipe.category}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right font-mono text-slate-300">
                        {formatCurrency(recipe.total_cost, currency)}
                      </td>
                      <td className="py-4 px-6 text-right font-mono text-white font-semibold">
                        {formatCurrency(recipe.menu_price, currency)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {getProfitIcon(recipe.plate_profit_percent, targetMargin)}
                          <span className={`font-mono font-bold text-lg ${getProfitColor(recipe.plate_profit_percent, targetMargin)}`}>
                            {recipe.plate_profit_percent}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDuplicate(recipe.id)}
                            disabled={duplicating === recipe.id}
                            className="hover:bg-slate-800 text-slate-400 hover:text-blue-500"
                            title="Duplicate Recipe"
                            data-testid={`duplicate-recipe-${recipe.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditRecipe(recipe)}
                            className="hover:bg-slate-800 text-slate-400 hover:text-amber-500"
                            data-testid={`edit-recipe-${recipe.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(recipe)}
                            className="hover:bg-red-500/10 text-slate-400 hover:text-red-500"
                            data-testid={`delete-recipe-${recipe.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Recipe Modal */}
      <RecipeModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        recipe={editingRecipe}
        currency={currency}
        targetMargin={targetMargin}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t("deleteDialog.recipeTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {t("deleteDialog.recipeDesc", { name: recipeToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-button"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Menu Panel */}
      <ShareMenuPanel
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        restaurantName={settings?.restaurant_name || settings?.full_name}
      />
    </div>
  );
};

export default RecipeBank;
